// Diamond Verification — main page.
// Heavy components live under /app/frontend/src/components/diamond-verification/.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Search, RotateCcw, Gem,
  CheckCircle2, FlaskConical, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

import { API, token } from '../components/diamond-verification/constants';
import { Wizard } from '../components/diamond-verification/Wizard';
import { TagCard } from '../components/diamond-verification/TagCard';
import { TagDetail } from '../components/diamond-verification/TagDetail';
import { ReportView } from '../components/diamond-verification/ReportView';
import GlobalNavigationMenu from '../components/GlobalNavigationMenu';
import { isHomePathForRole } from '../utils/roleHome';

export default function DiamondVerification() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { currentUser } = useAuth();
  const isPrivileged  = ['admin', 'owner'].includes(currentUser?.role);
  const canBatch      = ['admin', 'production_supervisor', 'metal_inventory_assistant'].includes(currentUser?.role);
  // MIA (and diamond_dept) can land on this page directly — hide the back
  // arrow in that case to avoid navigating to an empty history entry.
  const isHome = isHomePathForRole(location.pathname, currentUser?.role);

  const [tags,    setTags]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard,  setWizard]  = useState(null);
  const [report,  setReport]  = useState(false);
  const [detail,  setDetail]  = useState(null);
  const [tab,     setTab]     = useState('all');

  // Multi-select + batch creation
  const [selectMode,    setSelectMode]    = useState(false);
  const [selected,      setSelected]      = useState([]);   // array of tag.id
  const [batchModal,    setBatchModal]    = useState(false);
  const [assistants,    setAssistants]    = useState([]);
  const [batchForm,     setBatchForm]     = useState({
    lab: 'GIA', assigned_to_id: '',
    memo_no: '', report_service: 'MJG+JGC',
  });
  const [batchSaving,   setBatchSaving]   = useState(false);

  const loadTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/diamond-verification/`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setTags(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTags(); }, [loadTags]);

  const loadAssistants = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/lab-batches/assistants`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setAssistants(res.data);
    } catch { /* ignore */ }
  }, []);

  const filtered = tab === 'all' ? tags :
    tab === 'in_lab'  ? tags.filter(t => t.status === 'complete' && t.lab_status === 'in_lab') :
    /* complete */      tags.filter(t => t.status === 'complete' && t.lab_status === 'done');

  const deleteTag = useCallback(async (tag) => {
    if (!isPrivileged) return;
    if (!window.confirm(`Delete tag #${tag.tag_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/diamond-verification/${tag.id}`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setTags(prev => prev.filter(t => t.id !== tag.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed');
    }
  }, [isPrivileged]);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openBatchModal = async () => {
    await loadAssistants();
    setBatchForm({ lab: 'GIA', assigned_to_id: '', memo_no: '', report_service: 'MJG+JGC' });
    setBatchModal(true);
  };

  const createBatch = async () => {
    if (!batchForm.assigned_to_id) { alert('Please select an assistant'); return; }
    setBatchSaving(true);
    try {
      const res = await axios.post(`${API}/api/lab-batches/`, {
        lab: batchForm.lab,
        tag_ids: selected,
        assigned_to_id: batchForm.assigned_to_id,
        challan_details: {
          memo_no: batchForm.memo_no,
          report_service: batchForm.report_service,
        },
      }, { headers: { Authorization: `Bearer ${token()}` } });
      setBatchModal(false);
      setSelectMode(false);
      setSelected([]);
      loadTags();
      navigate(`/lab-batches/${res.data.id}`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to create batch');
    } finally {
      setBatchSaving(false);
    }
  };

  if (wizard) return (
    <Wizard
      tag={wizard === 'new' ? null : wizard}
      onClose={() => setWizard(null)}
      onSaved={loadTags}
    />
  );

  if (detail) return (
    <TagDetail
      tag={detail}
      onClose={() => setDetail(null)}
      onSaved={loadTags}
      onReopen={(t) => { setDetail(null); setWizard(t); }}
    />
  );

  if (report) return <ReportView onClose={() => setReport(false)} />;

  return (
    <div className="flex flex-col h-screen bg-gray-50 max-w-md mx-auto">
      {/* Header */}
      <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 shrink-0">
        {(!isHome || selectMode) ? (
          <button
            onClick={() => { if (selectMode) { setSelectMode(false); setSelected([]); } else navigate(-1); }}
            className="p-2 rounded-full hover:bg-emerald-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <div className="w-9 h-9 flex items-center justify-center shrink-0" />
        )}
        <div className="flex-1">
          <p className="text-xs text-emerald-200 font-medium">Production</p>
          <p className="text-base font-bold">
            {selectMode ? `${selected.length} selected` : 'Diamond Verification'}
          </p>
        </div>
        {canBatch && !selectMode && (
          <button
            onClick={() => { setSelectMode(true); setTab('in_lab'); }}
            className="px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-xs font-bold"
            data-testid="select-mode-btn"
          >
            Select
          </button>
        )}
        {!selectMode && (
          <>
            <button onClick={() => setReport(true)} className="p-2 rounded-full hover:bg-emerald-600" data-testid="report-btn">
              <Search className="w-5 h-5" />
            </button>
            <button onClick={() => setWizard('new')} className="p-2 rounded-full bg-white/20 hover:bg-white/30" data-testid="new-tag-btn">
              <Plus className="w-5 h-5" />
            </button>
          </>
        )}
        <GlobalNavigationMenu />
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 bg-emerald-800 text-white text-center divide-x divide-emerald-700">
        {[
          { label: 'Total', count: tags.length, key: 'all' },
          { label: 'In Lab', count: tags.filter(t => t.status === 'complete' && t.lab_status === 'in_lab').length, key: 'in_lab' },
          { label: 'Complete', count: tags.filter(t => t.status === 'complete' && t.lab_status === 'done').length, key: 'complete' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setTab(s.key)}
            data-testid={`dv-tab-${s.key}`}
            className={`py-2 transition-all ${tab === s.key ? 'bg-white/15' : ''}`}
          >
            <p className="text-lg font-bold">{s.count}</p>
            <p className="text-[10px] text-emerald-300 font-medium uppercase">{s.label}</p>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-24">
        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <RotateCcw className="w-8 h-8 mx-auto mb-2 animate-spin opacity-40" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Gem className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-semibold">No tags yet</p>
            <p className="text-sm text-gray-400 mt-1">Tap + to create the first one</p>
          </div>
        ) : (
          filtered.map(t => {
            const isSelected = selected.includes(t.id);
            const isEligibleForBatch = t.status === 'complete' && t.lab_status === 'in_lab' && !t.sent_to_lab;
            const canSelect  = selectMode && isEligibleForBatch;
            return (
              <div key={t.id} className={`relative mb-3 ${selectMode && !isEligibleForBatch ? 'opacity-40' : ''}`}>
                {selectMode && isEligibleForBatch && (
                  <div className={`absolute -left-1 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected ? 'border-emerald-600 bg-emerald-600' : 'border-gray-300 bg-white'
                  }`}>
                    {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white fill-current" />}
                  </div>
                )}
                {selectMode && t.sent_to_lab && (
                  <div className="absolute -left-1 top-1/2 -translate-y-1/2 z-10 text-[8px] font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded">
                    In Lab
                  </div>
                )}
                <div className={`${selectMode && isEligibleForBatch ? 'ml-6' : ''}`}>
                  <TagCard
                    tag={t}
                    onClick={() => {
                      if (canSelect) toggleSelect(t.id);
                      else if (!selectMode && t.status === 'complete') {
                        setDetail(t);
                      }
                    }}
                    onDelete={selectMode ? null : deleteTag}
                    isPrivileged={isPrivileged}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Lab Batch floating bar */}
      {selectMode && selected.length > 0 && (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 px-4 py-3 flex gap-2 shadow-lg z-40">
          <button
            onClick={() => { setSelected([]); }}
            className="px-4 py-2.5 rounded-2xl border border-gray-200 text-gray-500 text-sm font-semibold"
          >
            Clear
          </button>
          <button
            onClick={openBatchModal}
            className="flex-1 py-2.5 bg-emerald-600 text-white font-bold rounded-2xl text-sm active:bg-emerald-700 flex items-center justify-center gap-2"
            data-testid="create-batch-btn"
          >
            <FlaskConical className="w-4 h-4" />
            Create Lab Batch ({selected.length})
          </button>
        </div>
      )}

      {/* Batch creation modal */}
      {batchModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40">
          <div className="bg-white rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-lg">Create Lab Batch</p>
              <button onClick={() => setBatchModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <div className="space-y-3">
              {/* Lab picker — GIA + IGI */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Lab</p>
                <div className="grid grid-cols-2 gap-2">
                  {['GIA', 'IGI'].map(lab => (
                    <button
                      key={lab}
                      onClick={() => setBatchForm(f => ({ ...f, lab }))}
                      className={`py-2.5 rounded-xl text-sm font-bold transition-colors ${batchForm.lab === lab ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                      data-testid={`lab-${lab.toLowerCase()}`}
                    >
                      {lab}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1 italic">HRD/SGL coming later. TDS tags skip the batch process and are marked complete after the DV wizard.</p>
              </div>

              {/* Assign to */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Assign to Assistant</p>
                <select
                  value={batchForm.assigned_to_id}
                  onChange={e => setBatchForm(f => ({ ...f, assigned_to_id: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400"
                  data-testid="batch-assign-select"
                >
                  <option value="">Select assistant…</option>
                  {assistants.map(a => (
                    <option key={a.id} value={a.id}>{a.username}</option>
                  ))}
                </select>
                {assistants.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No metal inventory assistants found. Create one under People first.</p>
                )}
              </div>

              {/* Challan fields — only Memo No + Report Service */}
              {[
                { key: 'report_service',    label: 'Report Service',      placeholder: 'MJG+JGC' },
                { key: 'memo_no',           label: 'Memo No. (optional)', placeholder: '' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{label}</p>
                  <input
                    value={batchForm[key] || ''}
                    onChange={e => setBatchForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-emerald-400"
                    data-testid={`batch-form-${key}`}
                  />
                </div>
              ))}

              <div className="pt-1 pb-2 text-xs text-gray-400 border-t border-gray-100">
                {selected.length} tag{selected.length !== 1 ? 's' : ''} will stay editable in Diamond Verification until physically dispatched to lab.
              </div>

              <button
                onClick={createBatch}
                disabled={batchSaving || !batchForm.assigned_to_id}
                className="w-full py-3 bg-emerald-600 text-white font-bold rounded-2xl disabled:opacity-50 active:bg-emerald-700 flex items-center justify-center gap-2"
                data-testid="confirm-create-batch-btn"
              >
                <FlaskConical className="w-4 h-4" />
                {batchSaving ? 'Creating…' : `Create Batch (${selected.length} tags)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
